import type { ReactElement } from 'react';
import {
  useTable,
  List,
  ShowButton,
  getDefaultSortOrder,
  FilterDropdown,
} from '@refinedev/antd';
import type { HttpError, CrudFilters } from '@refinedev/core';
import {
  Table,
  Tag,
  Space,
  Input,
  Select,
  Tooltip,
  Checkbox,
  List as AntList,
  Card,
  Grid,
} from 'antd';
import {
  EyeOutlined,
  FormOutlined,
  FolderOpenOutlined,
} from '@ant-design/icons';

import {
  calculatePrice,
  formatPrice,
  type PageCountBookType,
} from '@woolaby/types';

import type { AdminOrder, OrderStatus } from '@/types';
import { ORDER_STATUSES } from '@/types';
import { hasStorageOutput } from '@/utils/storage';

import { ShareLinkButton } from './ShareLinkButton';

const { useBreakpoint } = Grid;

// ---------------------------------------------------------------------------
// Status color mapping
// ---------------------------------------------------------------------------

const STATUS_COLORS: Record<OrderStatus, string> = {
  INIT: 'default',
  STARTED: 'gold',
  PAYMENT_PENDING: 'gold',
  PAYMENT_COMPLETED: 'blue',
  PAYMENT_FAILED: 'red',
  GENERATING: 'processing',
  GENERATING_PLAN: 'processing',
  GENERATING_CHARACTER_SHEET: 'processing',
  GENERATING_STORYBOARD: 'processing',
  GENERATING_FINAL: 'processing',
  PLAN_REVIEW: 'orange',
  CHARACTER_SHEET_REVIEW: 'orange',
  STORYBOARD_REVIEW: 'orange',
  GENERATION_COMPLETE: 'green',
  GENERATION_FAILED: 'red',
  PREVIEW_READY: 'green',
  DRAFT_READY: 'lime',
  CANCELLED: 'default',
  PRINT_JOB_SUBMITTED: 'cyan',
  SHIPPED: 'green',
};

/** Statuses where preview content is available */
const PREVIEW_ELIGIBLE_STATUSES: OrderStatus[] = [
  'PREVIEW_READY',
  'GENERATION_COMPLETE',
  'PRINT_JOB_SUBMITTED',
  'SHIPPED',
];

/** Statuses where confirmation (survey) data is still on the order */
const CONFIRMATION_ELIGIBLE_STATUSES: OrderStatus[] = [
  'STARTED',
  'PAYMENT_PENDING',
  'PAYMENT_COMPLETED',
  'GENERATING',
  'GENERATING_PLAN',
  'GENERATING_CHARACTER_SHEET',
  'GENERATING_STORYBOARD',
  'GENERATING_FINAL',
  'PLAN_REVIEW',
  'CHARACTER_SHEET_REVIEW',
  'STORYBOARD_REVIEW',
  'GENERATION_COMPLETE',
  'GENERATION_FAILED',
  'PREVIEW_READY',
  'PRINT_JOB_SUBMITTED',
  'SHIPPED',
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const SUPABASE_STORAGE_URL: string | undefined = import.meta.env
  .VITE_SUPABASE_STORAGE_URL;

interface SearchForm {
  q: string;
  skipUnpaid: boolean;
  skipInit: boolean;
}

function formatCents(cents: number | null): string {
  if (cents === null || cents === undefined) return '-';
  return `$${(cents / 100).toFixed(2)}`;
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleString();
}

// ---------------------------------------------------------------------------
// Mobile card component
// ---------------------------------------------------------------------------

interface MobileOrderCardProps {
  record: AdminOrder;
}

function MobileOrderCard({ record }: MobileOrderCardProps): ReactElement {
  const canPreview = PREVIEW_ELIGIBLE_STATUSES.includes(record.status);
  const canConfirm = CONFIRMATION_ELIGIBLE_STATUSES.includes(record.status);

  return (
    <Card
      size="small"
      style={{ width: '100%' }}
      extra={
        <Tag color={STATUS_COLORS[record.status] ?? 'default'}>
          {record.status}
        </Tag>
      }
    >
      <Space direction="vertical" size={4} style={{ width: '100%' }}>
        <div>
          <strong>Email:</strong> {record.email ?? '—'}
        </div>
        <div>
          <strong>Book Title:</strong> {record.bookTitle ?? '—'}
        </div>
        <div>
          <strong>Age:</strong> {record.age ?? '—'}
        </div>
        <div>
          <strong>Order Type:</strong> {record.paidProductType ?? '—'}
        </div>
        <div>
          <strong>Created:</strong> {formatDate(record.createdAt)}
        </div>
        <Space size="small" wrap>
          {canPreview && (
            <ShareLinkButton
              path={`/book/preview/${record.id}`}
              type="link"
              size="small"
              icon={<EyeOutlined />}
              label="Preview"
              tooltip="Open preview in client-fluffy"
            />
          )}
          {canConfirm && (
            <ShareLinkButton
              path={`/book/confirm/${record.id}`}
              type="link"
              size="small"
              icon={<FormOutlined />}
              label="Confirm"
              tooltip="Open confirmation in client-fluffy"
            />
          )}
        </Space>
      </Space>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function OrderList(): ReactElement {
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const { tableProps, sorters, searchFormProps } = useTable<
    AdminOrder,
    HttpError,
    SearchForm
  >({
    resource: 'orders',
    syncWithLocation: true,
    sorters: {
      initial: [{ field: 'createdAt', order: 'desc' }],
    },
    onSearch: (values) => {
      const filters: CrudFilters = [
        {
          field: 'q',
          operator: 'contains',
          value: values.q || undefined,
        },
        {
          field: 'skipUnpaid',
          operator: 'eq',
          value: values.skipUnpaid ? 'true' : undefined,
        },
        {
          field: 'skipInit',
          operator: 'eq',
          value: values.skipInit ? 'true' : undefined,
        },
      ];
      return filters;
    },
  });

  return (
    <List>
      <Space
        style={{ marginBottom: 16, display: 'flex', flexWrap: 'wrap' }}
        size="middle"
      >
        <form
          {...searchFormProps}
          onSubmit={(e) => {
            e.preventDefault();
            searchFormProps.form?.submit();
          }}
          style={{ display: 'flex', gap: 8, alignItems: 'center' }}
        >
          <Input.Search
            placeholder="Search email / phone"
            allowClear
            onSearch={(value) => {
              searchFormProps.form?.setFieldsValue({ q: value });
              searchFormProps.form?.submit();
            }}
            style={{ width: 280 }}
          />
          <Checkbox
            onChange={(e) => {
              searchFormProps.form?.setFieldsValue({
                skipUnpaid: e.target.checked,
              });
              searchFormProps.form?.submit();
            }}
          >
            Skip unpaid
          </Checkbox>
          <Checkbox
            onChange={(e) => {
              searchFormProps.form?.setFieldsValue({
                skipInit: e.target.checked,
              });
              searchFormProps.form?.submit();
            }}
          >
            Skip init
          </Checkbox>
        </form>
      </Space>

      {isMobile ? (
        <AntList
          dataSource={tableProps.dataSource ? [...tableProps.dataSource] : []}
          pagination={
            tableProps.pagination && typeof tableProps.pagination === 'object'
              ? {
                  current: tableProps.pagination.current,
                  pageSize: tableProps.pagination.pageSize,
                  total: tableProps.pagination.total,
                  onChange: tableProps.pagination.onChange,
                }
              : false
          }
          renderItem={(record) => (
            <AntList.Item key={record.id} style={{ padding: '8px 0' }}>
              <MobileOrderCard record={record} />
            </AntList.Item>
          )}
        />
      ) : null}
      {!isMobile ? (
        <Table {...tableProps} rowKey="id" scroll={{ x: 1600 }} size="small">
          <Table.Column<AdminOrder>
            dataIndex="bookTitle"
            title="Book Title"
            render={(value: string | null) => value ?? '-'}
            width={180}
            ellipsis
          />

          <Table.Column<AdminOrder>
            dataIndex="age"
            title="Age"
            width={60}
            render={(age: number | null) => age ?? '—'}
          />

          <Table.Column<AdminOrder>
            dataIndex="email"
            title="Email"
            render={(value: string | null) => value ?? '-'}
            width={180}
            ellipsis
          />

          <Table.Column<AdminOrder>
            dataIndex="phone"
            title="Phone"
            render={(value: string | null) => value ?? '-'}
            width={130}
          />

          <Table.Column<AdminOrder>
            dataIndex="status"
            title="Status"
            sorter
            defaultSortOrder={getDefaultSortOrder('status', sorters)}
            render={(value: OrderStatus) => (
              <Tag color={STATUS_COLORS[value] ?? 'default'}>{value}</Tag>
            )}
            filterDropdown={(props) => (
              <FilterDropdown {...props}>
                <Select
                  mode="multiple"
                  placeholder="Filter by status"
                  style={{ minWidth: 200 }}
                  options={ORDER_STATUSES.map((s) => ({ label: s, value: s }))}
                />
              </FilterDropdown>
            )}
            width={200}
          />

          <Table.Column<AdminOrder>
            dataIndex="paymentStatus"
            title="Payment"
            render={(value: string | null) =>
              value ? (
                <Tag color={value === 'paid' ? 'green' : 'orange'}>{value}</Tag>
              ) : (
                '-'
              )
            }
            width={100}
          />

          <Table.Column<AdminOrder>
            dataIndex="paidProductType"
            title="Order Type"
            render={(value: string | null) => value ?? '-'}
            width={100}
          />

          <Table.Column<AdminOrder>
            dataIndex="pageCount"
            title="Pages"
            render={(value: number | null) => value ?? '-'}
            width={70}
            align="center"
          />

          <Table.Column<AdminOrder>
            title="Selling Price"
            render={(_: unknown, record: AdminOrder) => {
              if (!record.paidProductType || record.pageCount === null)
                return '-';
              return formatPrice(
                calculatePrice(
                  record.paidProductType as PageCountBookType,
                  record.pageCount
                )
              );
            }}
            width={100}
            align="right"
          />

          <Table.Column<AdminOrder>
            dataIndex="usedFallbackModel"
            title="Fallback"
            render={(value: boolean) =>
              value ? <Tag color="orange">Fallback</Tag> : '-'
            }
            filterDropdown={(props) => (
              <FilterDropdown {...props}>
                <Select
                  placeholder="Fallback model"
                  style={{ minWidth: 140 }}
                  allowClear
                  options={[
                    { label: 'Yes', value: 'true' },
                    { label: 'No', value: 'false' },
                  ]}
                />
              </FilterDropdown>
            )}
            width={90}
            align="center"
          />

          <Table.Column<AdminOrder>
            dataIndex="aiCostCents"
            title="AI Cost"
            render={(value: number | null) => formatCents(value)}
            sorter
            defaultSortOrder={getDefaultSortOrder('aiCostCents', sorters)}
            width={90}
            align="right"
          />

          <Table.Column<AdminOrder>
            dataIndex="luluPrintCostCents"
            title="Print Cost"
            render={(value: number | null) => formatCents(value)}
            width={90}
            align="right"
          />

          <Table.Column<AdminOrder>
            dataIndex="luluShipCostCents"
            title="Ship Cost"
            render={(value: number | null) => formatCents(value)}
            width={90}
            align="right"
          />

          <Table.Column<AdminOrder>
            dataIndex="workflowRunId"
            title="Workflow Run"
            render={(value: string | null) => value ?? '-'}
            width={120}
            ellipsis
          />

          <Table.Column<AdminOrder>
            title="Preview"
            render={(_: unknown, record: AdminOrder) =>
              PREVIEW_ELIGIBLE_STATUSES.includes(record.status) ? (
                <ShareLinkButton
                  path={`/book/preview/${record.id}`}
                  type="link"
                  size="small"
                  icon={<EyeOutlined />}
                  tooltip="Open preview in client-fluffy"
                />
              ) : (
                '-'
              )
            }
            width={90}
            align="center"
          />

          <Table.Column<AdminOrder>
            title="Confirm"
            render={(_: unknown, record: AdminOrder) =>
              CONFIRMATION_ELIGIBLE_STATUSES.includes(record.status) ? (
                <ShareLinkButton
                  path={`/book/confirm/${record.id}`}
                  type="link"
                  size="small"
                  icon={<FormOutlined />}
                  tooltip="Open confirmation in client-fluffy"
                />
              ) : (
                '-'
              )
            }
            width={90}
            align="center"
          />

          <Table.Column<AdminOrder>
            title="Storage"
            render={(_: unknown, record: AdminOrder) =>
              hasStorageOutput(record) ? (
                SUPABASE_STORAGE_URL ? (
                  <Tooltip title="Open Supabase Storage folder">
                    <a
                      href={`${SUPABASE_STORAGE_URL}?prefix=${record.id}/`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <FolderOpenOutlined />
                    </a>
                  </Tooltip>
                ) : (
                  <Tooltip title="Set VITE_SUPABASE_STORAGE_URL to enable the link">
                    <FolderOpenOutlined />
                  </Tooltip>
                )
              ) : (
                '-'
              )
            }
            width={70}
            align="center"
          />

          <Table.Column<AdminOrder>
            dataIndex="createdAt"
            title="Created At"
            sorter
            defaultSortOrder={getDefaultSortOrder('createdAt', sorters)}
            render={(value: string) => formatDate(value)}
            width={160}
          />

          <Table.Column<AdminOrder>
            title="Actions"
            fixed="right"
            width={80}
            render={(_: unknown, record: AdminOrder) => (
              <ShowButton hideText size="small" recordItemId={record.id} />
            )}
          />
        </Table>
      ) : null}
    </List>
  );
}
